"use client";

import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  ChevronLeft,
  Info,
  Palette,
  Rocket,
  SlidersHorizontal,
  Smartphone,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";

import EditOrganisationDialog from "@/components/dialogs/tenants/organisation/EditOrganisation";
import CreateCampaignDialog from "@/components/dialogs/tenants/campaign/CreateCampaignDialog";
import AddExperienceDialog from "@/components/dialogs/tenants/experiences/AddExperienceDialog";
import TenantHero from "@/components/tenants/profile/TenantHero";
import CampaignCard from "@/components/tenants/profile/CampaignCard";
import BrandingTab from "@/components/tenants/profile/BrandingTab";
import EditCampaignDialog from "@/components/dialogs/tenants/campaign/EditCampaignDialog";
import {
  useCreateAppOnboardingMutation,
  useGetAppOnboardingByTenantIdQuery,
  useGetCampaignsByTenantIdQuery,
  useUpdateTenantBrandingMutation,
} from "@/store/slices/apiSlice";
import CircleLoader from "@/components/common/ActionLoader";

import { useGetTenantByIdQuery } from "@/store/slices/apiSlice";
import SuccessModal from "@/components/tenants/SuccessModal";
import BrandColorsDialog from "@/components/tenants/profile/BrandColorsDialog";
import {
  appOnBoardingIcon,
  brandingIcon,
  CampaignIcon,
  onBoardingIcon,
  organizastionIcon,
} from "@/lib/Images";
import { formatDate } from "@/lib/utils";
import AlertMsg from "@/components/dialogs/common/AlertMsg";
import { initAlertMsg } from "@/lib/constant/constant";

export default function TenantProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [showBrandingRequiredModal, setShowBrandingRequiredModal] =
    useState(false);

  const tenantId = location.state?.tenantId;
  const onboardingConfiguration = location.state?.onboardingConfiguration;
  useEffect(() => {
    if (!tenantId) {
      navigate("/tenants", { replace: true });
    }
  }, [tenantId, navigate]);
  const {
    data,
    isLoading: isTenantLoading,
    refetch,
  } = useGetTenantByIdQuery(tenantId, {
    skip: !tenantId,
    refetchOnMountOrArgChange: true,
  });

  const tenantDetails = data?.response?.data;
  // console.log("tenant", onboardingConfiguration);
  const isBrandingConfigured =
    !!tenantDetails?.branding?.logoUrl &&
    !!tenantDetails?.branding?.primaryColor;
  // const { data: campaignList, isLoading, error } = useGetCampaignsQuery();
  const {
    data: campaignList,
    isLoading,
    error,
    refetch: refetchCampaigns,
  } = useGetCampaignsByTenantIdQuery(
    { tenantId: tenantId },
    {
      skip: !tenantId,
    },
  );
  const campaignDetails = campaignList?.response?.data;
  const [activeTab, setActiveTab] = useState("branding");
  const [openEditOrganisation, setOpenEditOrganisation] = useState(false);
  const [openCreateCampaign, setOpenCreateCampaign] = useState(false);
  const [openAddExperience, setOpenAddExperience] = useState(false);
  const [alertMsg, setAlertMsg] = useState(initAlertMsg);
  const [openEditCampaign, setOpenEditCampaign] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const [openBrandDialog, setOpenBrandDialog] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [primaryColor, setPrimaryColor] = useState("#00D3F2");
  const [secondaryColor, setSecondaryColor] = useState("#000000");
  const [showTrackingCard, setShowTrackingCard] = useState(true);
  const [showSetupSteps, setShowSetupSteps] = useState(false);
  const [createdCampaign, setCreatedCampaign] = useState(null);

  const {
    data: appOnboardingData,
    isLoading: isAppOnboardingLoading,
    refetch: refetchAppOnboarding,
  } = useGetAppOnboardingByTenantIdQuery(
    { tenantId: tenantId },
    {
      skip: !tenantId,
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state]);

  const hasBranding =
    !!tenantDetails?.branding?.logoUrl &&
    !!tenantDetails?.branding?.primaryColor;

  const onboardingData = hasBranding
    ? appOnboardingData?.response?.data || []
    : [];

  const isOnboardingData = hasBranding;

  // console.log("first", isOnboardingData);

  const onboardingList = Array.isArray(onboardingConfiguration)
    ? onboardingConfiguration
    : onboardingConfiguration
      ? [onboardingConfiguration]
      : [];

  const [createAppOnboarding] = useCreateAppOnboardingMutation();

  const handleOpenOnboardingEditor = () => {
    if (!hasBranding) {
      setShowBrandingRequiredModal(true);
      return;
    }

    setActiveTab("onboarding");
  };

  const previousSetupStep = tenantDetails?.setupStep;

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch (error) {
      console.error("Copy failed:", error);

      return false;
    }
  };

  const handleAlertClose = () => {
    setAlertMsg(initAlertMsg);
  };

  const campaigns = Array.isArray(campaignList?.response?.data)
    ? campaignList.response.data
    : [];
  const totalCampaigns = campaigns.length;

  const setupStep = Math.min(
    Math.max(Number(tenantDetails?.setupStep) || 1, 1),
    4,
  );

  const isOnboardingConfigured =
    hasBranding &&
    onboardingData.length > 0 &&
    onboardingData.some((item) => item.status === "ACTIVE");

  const steps = [
    {
      id: 1,
      label: "Organisation Setup",
      completed: setupStep >= 1,
      icon: (
        <img
          src={organizastionIcon}
          alt="organization"
          className="h-4 w-4 md:h-7 md:w-7"
        />
      ),
    },
    {
      id: 2,
      label: "Add Branding",
      completed: setupStep >= 2,
      action: setupStep === 1 ? "Add Now" : null,
      onClick: () => {
        setOpenBrandDialog(true);
      },
      icon: (
        <img
          src={brandingIcon}
          alt="Branding"
          className="h-4 w-4 md:h-7 md:w-7"
        />
      ),
    },
    {
      id: 3,
      label: "Setup App Onboarding",
      completed: setupStep >= 3,
      action: setupStep === 2 ? "Add Now" : null,
      onClick: handleOpenOnboardingEditor,
      icon: (
        <img
          src={appOnBoardingIcon}
          alt="Onboarding"
          className="h-4 w-4 md:h-7 md:w-7"
        />
      ),
    },
    {
      id: 4,
      label: "Create Campaign\n& Add Experience",
      completed: setupStep >= 4,
      action: setupStep === 3 ? "Add Now" : null,
      onClick: () => {
        setActiveTab("campaigns");

        if (totalCampaigns === 0) {
          setOpenCreateCampaign(true);
        }
      },
      icon: (
        <img
          src={CampaignIcon}
          alt="Campaign"
          className="h-4 w-4 md:h-7 md:w-7"
        />
      ),
    },
  ];

  const completedSteps = Math.min(setupStep, steps.length);

  const progressPercentage = Math.round((completedSteps / steps.length) * 100);

  const firstIncompleteStepIndex = steps.findIndex((step) => !step.completed);

  const activeStepIndex =
    firstIncompleteStepIndex === -1
      ? steps.length - 1
      : firstIncompleteStepIndex;

  const isSetupCompleted = setupStep >= steps.length;

  useEffect(() => {
    if (!tenantId) return;

    const isHidden = localStorage.getItem(`tenant-tracking-card-${tenantId}`);

    if (setupStep >= steps.length && isHidden === "hidden") {
      setShowTrackingCard(false);
    } else {
      setShowTrackingCard(true);
    }
  }, [tenantId, setupStep]);

  const handleCloseTrackingCard = () => {
    localStorage.setItem(`tenant-tracking-card-${tenantId}`, "hidden");
    setShowTrackingCard(false);
  };

  if (isTenantLoading || !tenantDetails) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <CircleLoader loading={isTenantLoading} size={64} />
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 py-6 text-neutral-100">
      <Button
        type="button"
        variant="neutral"
        onClick={() => navigate("/tenants", { replace: true })}
        className="mb-7 rounded-md border border-brand-20  text-xs font-semibold text-[#7EDBFF] hover:bg-white/5"
      >
        <ChevronLeft className="h-4 w-4" />
        Back
      </Button>

      <TenantHero
        tenant={tenantDetails}
        onEdit={() => setOpenEditOrganisation(true)}
      />

      {showTrackingCard && (
        <section className="relative mt-4 overflow-hidden rounded-lg border border-[#2EC2C282] bg-gradient-to-r from-[#123F4F] via-[#224D65] to-[#30345F]">
          <div className="grid min-h-[205px] grid-cols-1 lg:grid-cols-[360px_1fr]">
            {/* Left information */}
            <div className="gap-3 px-9 py-7">
              <div className="flex items-center gap-2">
                <Info
                  className="h-7 w-7 shrink-0 text-[#00D3F2]"
                  strokeWidth={2}
                />

                <p className="text-xl font-bold text-[#00D3F2]">
                  {progressPercentage}% complete
                </p>
              </div>
              <div>
                <p className="mt-3 text-base font-semibold text-white">
                  {isSetupCompleted
                    ? "Tenant Ready for Launch"
                    : "This tenant is not yet ready for launch."}
                </p>

                <p className="mt-2 max-w-[300px] text-sm leading-[1.55] text-white">
                  {isSetupCompleted
                    ? "The tenant is now live and ready for users. Users can access the platform through the app link shared by their organisation or download the app directly from the Apple App Store or Google Play Store. They can then sign in using their authorised work email to access available experiences."
                    : "Complete all required setup steps, including branding, app onboarding, campaigns and experiences to launch."}
                </p>

                {!isSetupCompleted && (
                  <p className="max-w-[300px] text-sm leading-[1.55] text-white">
                    <span className="font-bold text-white">Important:</span> End
                    users cannot sign in until setup is complete.
                  </p>
                )}
              </div>
            </div>

            {/* Right progress steps */}
            <div className="relative flex items-center px-8 py-7">
              <div className="grid w-full grid-cols-4">
                {steps.map((step, index) => {
                  const isCompleted = step.completed;
                  const isActive =
                    !isSetupCompleted && index === activeStepIndex;

                  return (
                    <div
                      key={step.id}
                      className="relative flex min-w-0 flex-col items-center text-center"
                    >
                      {/* Connector */}
                      {index < steps.length - 1 && (
                        <div
                          className={`absolute left-1/2 md:top-[35px] top-[15px] z-0 h-[2px] w-full ${
                            isCompleted && steps[index + 1]?.completed
                              ? "bg-[#00C950]"
                              : isCompleted
                                ? "bg-gradient-to-r from-[#00C950] to-white/45"
                                : "bg-white/45"
                          }`}
                        />
                      )}

                      {/* Step circle */}
                      <div
                        className={`relative z-10 flex md:h-[72px] md:w-[72px] h-7 w-7 items-center justify-center rounded-full border-2 ${
                          isCompleted
                            ? "border-[#00C950] bg-[#176A5D] text-white"
                            : isActive
                              ? "border-[#A8A8A8] bg-[#30485A] text-white"
                              : "border-[#A8A8A8] bg-[#304353] text-white"
                        }`}
                      >
                        {step.icon}

                        {isCompleted && (
                          <span className="absolute -right-1 top-2 flex md:h-6 md:w-6 h-3 w-3 items-center justify-center rounded-full bg-[#00C950] md:text-sm text-xs font-bold text-white">
                            ✓
                          </span>
                        )}
                      </div>

                      {/* Step label */}
                      <p className="mt-3 min-h-[38px] whitespace-pre-line px-2 md:text-base text-xs font-semibold leading-[1.25] text-white">
                        Step {step.id}: {step.label}
                      </p>

                      {/* Current action */}
                      {step.action && (
                        <button
                          type="button"
                          onClick={step.onClick}
                          className="mt-1 inline-flex h-6 items-center rounded-full border border-[#00D3F2] bg-[#123A4E] px-3 text-[11px] font-medium text-[#7EDBFF] transition hover:bg-[#16495F]"
                        >
                          {step.action}
                          <span className="ml-1 text-sm leading-none">›</span>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {isSetupCompleted && (
            <button
              type="button"
              // onClick={() => setShowTrackingCard(false)}
              onClick={handleCloseTrackingCard}
              className="absolute right-3 top-3 inline-flex items-center gap-2 rounded-md bg-[#72CFFC2E] px-2 py-1.5 text-xs sm:px-3 sm:py-2 sm:text-sm font-medium text-white transition hover:bg-white/10 hover:text-white"
              aria-label="Close setup progress"
            >
              <X className="h-4 w-4 shrink-0" />
              <span>Close</span>
            </button>
          )}
        </section>
      )}

      <div className="mt-8 border-b border-white/20">
        <div className="flex gap-8">
          <button
            type="button"
            onClick={() => setActiveTab("branding")}
            className={`pb-3 text-lg font-bold ${
              activeTab === "branding"
                ? "border-b-2 border-[#00D3F2] text-[#00D3F2]"
                : "text-neutral-100/80"
            }`}
          >
            Branding
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("onboarding")}
            className={`pb-3 text-lg font-bold ${
              activeTab === "onboarding"
                ? "border-b-2 border-[#00D3F2] text-[#00D3F2]"
                : "text-neutral-100/80"
            }`}
          >
            Application Onboarding
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("campaigns")}
            className={`pb-3 text-lg font-bold ${
              activeTab === "campaigns"
                ? "border-b-2 border-[#00D3F2] text-[#00D3F2]"
                : "text-neutral-100/80"
            }`}
          >
            Campaigns
          </button>
        </div>
      </div>
      {activeTab === "branding" && (
        <BrandingTab organisationDetails={tenantDetails} />
      )}

      {activeTab === "onboarding" && (
        <>
          {isAppOnboardingLoading ? (
            <div className="flex min-h-[400px] items-center justify-center">
              <CircleLoader loading size={64} />
            </div>
          ) : // ) : onboardingData?.length === 0 ? (
          // onboardingData.length === 0 && isOnboardingData === false ? (
          hasBranding && onboardingData.length > 0 ? (
            <section className="mt-8">
              {/* Second Image UI */}
              {onboardingData.map((item) => {
                const isOnboardingInactive = item.status === "INACTIVE";

                return (
                  <div
                    className="rounded-lg border border-brand-17 bg-brand-100 px-6 py-6 shadow-lg"
                    key={item._id}
                  >
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[140px_1fr_1px_290px] lg:items-center">
                      <div className="flex h-42 w-42 items-center justify-center rounded-xl">
                        <img
                          src={onBoardingIcon}
                          className="h-42 w-42 text-neutral-100/45"
                        />
                      </div>

                      <div>
                        <h2 className="text-2xl font-bold text-neutral-100">
                          {/* {experience.title} */}
                          {item.name}
                        </h2>

                        <p className="mt-1 text-sm text-neutral-100/60">
                          Created {formatDate(item.createdAt)}
                        </p>

                        <p className="mt-6 max-w-3xl text-sm leading-relaxed text-neutral-100/85">
                          {item.description}
                        </p>

                        <div className="mt-4 flex flex-wrap gap-3">
                          <StatusBadge status={item.status}>
                            Status:{" "}
                            {item.status === "INACTIVE" ? "Pending" : "ACTIVE"}
                          </StatusBadge>
                        </div>
                      </div>

                      <div className="hidden h-full w-px bg-[#695CA8] lg:block" />

                      <div className="flex flex-wrap gap-3 sm:items-end">
                        <ActionButton
                          onClick={() =>
                            navigate("/tenants/application-onboarding/edit", {
                              state: {
                                onboardingId: item._id || item.id,
                                tenantId,
                              },
                            })
                          }
                          icon={<SlidersHorizontal className="h-4 w-4" />}
                        >
                          {isOnboardingInactive
                            ? "Set Up Onboarding"
                            : "Edit Onboarding"}
                          {/* Edit Onboarding */}
                        </ActionButton>
                        {isOnboardingInactive && (
                          <div className="mt-2 flex items-center gap-2 text-sm font-medium text-[#FF6B6B]">
                            <Info className="h-4 w-4" />
                            <span>Complete onboarding setup first</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </section>
          ) : (
            <section className="mt-6">
              {/* First Image UI */}
              <div className="flex min-h-[540px] flex-col items-center justify-start pt-8 rounded-lg border border-dashed border-[#4E7A92] bg-[#0D2744] text-center">
                <img src={onBoardingIcon} alt="Onboarding" />
                <h2 className="text-xl font-bold leading-[140%] tracking-[0] text-white">
                  Set Up App Onboarding
                </h2>

                <p className="mt-4 max-w-2xl text-sm text-white/70">
                  Before users explore launch experiences, they will be
                  introduced to tenant’s platform through a series of onboarding
                  screens. Setup onboarding to welcome users, introduce tenant’s
                  organisation, explain the purpose of the platform and set
                  expectations for the training journey ahead.
                </p>

                <Button
                  className="mt-6 bg-brand-21"
                  onClick={handleOpenOnboardingEditor}
                >
                  Configure Onboarding Screens
                </Button>
              </div>
            </section>
          )}
        </>
      )}

      {activeTab === "campaigns" && (
        <section className="mt-8">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-sm font-medium text-neutral-100/80">
              Campaigns
            </h2>

            <Button
              type="button"
              // onClick={() => setOpenCreateCampaign(true)}
              // onClick={() => {
              //   if (!isBrandingConfigured) {
              //     setShowBrandingRequiredModal(true);
              //     return;
              //   }

              //   setOpenCreateCampaign(true);
              // }}
              onClick={() => {
                if (!isBrandingConfigured) {
                  setShowBrandingRequiredModal(true);
                  return;
                }

                if (!isOnboardingConfigured) {
                  setAlertMsg({
                    open: true,
                    isError: true,
                    label: "Onboarding Required",
                    msg: "Please complete application onboarding before creating a campaign.",
                    btnText: "OK",
                    canClose: false,
                  });
                  return;
                }

                setOpenCreateCampaign(true);
              }}
              className="h-9 rounded-md bg-brand-21 px-5 text-sm font-bold text-neutral-100 hover:bg-brand-22"
            >
              + Create New Campaign
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {isLoading ? (
              <div className="text-sm text-neutral-100/70">
                <CircleLoader size={64} loading={isLoading} />
              </div>
            ) : // error ? (
            //   <div className="text-sm text-neutral-100/70 text-center">
            //    No Data Available
            //   </div>
            // ) :
            campaignList?.response?.data?.length === 0 ? (
              <div className="text-sm text-neutral-100/70 text-center">
                No campaigns found
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                {campaignList?.response?.data?.map((campaign) => (
                  <CampaignCard
                    key={campaign._id}
                    campaign={{
                      id: campaign._id,
                      name: campaign.campaignName,
                      description: campaign.description,
                      status: campaign.status,
                      access: campaign.campaignAccessType,
                      createdAt: campaign.createdAt,
                      allowedDomains: campaign.allowedDomains,
                      invitedEmails: campaign.invitedEmails,
                      noOfUsers: campaign.noOfUsers,
                      noOfExperiences: campaign.noOfExperiences,
                    }}
                    deepLink={tenantDetails?.deeplinkText}
                    onCopy={handleCopy}
                    // onView={() => navigate("/tenants/campaigns/experiences")}
                    onView={() =>
                      navigate("/tenants/campaigns/experiences", {
                        state: {
                          campaignId: campaign._id || campaign.id,
                          campaignName: campaign.campaignName,
                          tenantId,
                          tenantName: tenantDetails?.orgName,
                          deepLink: tenantDetails?.deeplinkText,
                        },
                      })
                    }
                    // onAddExperience={() => setOpenAddExperience(true)}
                    onAddExperience={() => {
                      setSelectedCampaign(campaign);
                      setOpenAddExperience(true);
                    }}
                    onEditCampaign={() => {
                      setSelectedCampaign(campaign);
                      setOpenEditCampaign(true);
                    }}
                    onRemoveCampaign={() => {
                      // console.log("Remove campaign:", campaign);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <EditOrganisationDialog
        open={openEditOrganisation}
        onClose={() => setOpenEditOrganisation(false)}
        organisationDetails={tenantDetails}
        onSave={(payload) => {
          // console.log("Saved organisation:", payload);
        }}
      />

      <CreateCampaignDialog
        open={openCreateCampaign}
        onClose={() => setOpenCreateCampaign(false)}
        // campaignDetails={campaignDetails}
        tenantDetails={tenantDetails}
        tenantId={tenantId}
        // {selectedCampaign?.tenantId || selectedCampaign?.tenantId?._id}

        onSuccess={async (campaign) => {
          const shouldShowSetupSteps = Number(previousSetupStep) < 4;

          await Promise.all([
            refetch(),
            refetchCampaigns(),
            refetchAppOnboarding(),
          ]);

          setCreatedCampaign(campaign);
          setShowSetupSteps(shouldShowSetupSteps);

          setOpenCreateCampaign(false);

          // active tab change
          setActiveTab("campaigns");
        }}
        onAddExperience={() => {
          setOpenAddExperience(true);
        }}
        steps={steps}
        progress={progressPercentage}
        showSetupSteps={showSetupSteps}
      />

      <AddExperienceDialog
        open={openAddExperience}
        onClose={() => {
          setOpenAddExperience(false);
          setSelectedCampaign(null);
        }}
        tenantDetails={tenantDetails}
        tenantName={tenantDetails?.orgName}
        deepLink={tenantDetails?.deeplinkText}
        defaultCampaign={selectedCampaign?.campaignName || ""}
        campaignId={selectedCampaign?._id || ""}
        onSuccess={async () => {
          await Promise.all([
            refetch(),
            refetchCampaigns(),
            refetchAppOnboarding(),
          ]);
          setOpenAddExperience(false);
          setSelectedCampaign(null);
          setActiveTab("campaigns");
        }}
      />

      <EditCampaignDialog
        open={openEditCampaign}
        onClose={() => {
          setOpenEditCampaign(false);
          setSelectedCampaign(null);
        }}
        campaignDetails={selectedCampaign}
        tenantDetails={campaignDetails}
        onSuccess={(payload) => {
          // console.log("Campaign updated:", payload);
        }}
      />
      <SuccessModal
        open={showBrandingRequiredModal}
        onClose={() => setShowBrandingRequiredModal(false)}
        title="Branding Required!"
        description="Tenant branding is required before creating campaigns or experiences. Please upload the branding assets to continue."
        primaryButtonText="Upload Branding Assets"
        secondaryButtonText="Go Back"
        titleClassName="text-[#66BEFF]"
        iconType="warning"
        onPrimaryClick={() => {
          setShowBrandingRequiredModal(false);
          setOpenBrandDialog(true);
          // setActiveTab("branding");
        }}
        onSecondaryClick={() => {
          setShowBrandingRequiredModal(false);
        }}
      />

      <BrandColorsDialog
        tenantId={tenantId}
        open={openBrandDialog}
        onClose={() => setOpenBrandDialog(false)}
        logoFile={logoFile}
        setLogoFile={setLogoFile}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        setPrimaryColor={setPrimaryColor}
        setSecondaryColor={setSecondaryColor}
        onSave={async () => {
          await Promise.all([refetch(), refetchAppOnboarding()]);

          setOpenBrandDialog(false);
          setShowBrandingRequiredModal(false);
        }}
        // onSave={async () => {
        //   await refetch();

        //   setOpenBrandDialog(false);
        //   setShowBrandingRequiredModal(false);
        //   setOpenCreateCampaign(true);
        // }}
      />

      <AlertMsg
        label={alertMsg.label}
        msg={alertMsg.msg}
        open={alertMsg.open}
        onClose={handleAlertClose}
        confirmAction={handleAlertClose}
        isLoading={false}
        isError={alertMsg.isError}
        canClose={alertMsg.canClose}
        confirmationBtnText={alertMsg.btnText}
      />
    </div>
  );
}
const StatusBadge = ({ status, children }) => {
  const isPublished = status === "PUBLISHED";
  const isPending = status === "INACTIVE";

  return (
    <span
      className={`rounded-md px-3 py-2 text-xs font-bold ${
        isPending || isPublished
          ? // ? "bg-[#064D23] text-[#00E261]"
            "bg-[#4A1D1D] text-[#FF6B6B]"
          : "bg-[#064D23] text-[#00E261]"
      }`}
    >
      {children}
    </span>
  );
};
const ActionButton = ({ children, icon, onClick, disabled, danger }) => {
  return (
    <Button
      type="button"
      variant="outline"
      disabled={disabled}
      onClick={onClick}
      className={`h-8 w-full justify-start border-[#00A9D6] bg-transparent px-4 text-xs font-bold hover:bg-white/5 sm:w-[170px] ${
        danger
          ? "text-[#FF4D5D] hover:text-[#FF4D5D]"
          : "text-neutral-100 hover:text-neutral-100"
      } ${disabled ? "cursor-not-allowed opacity-40" : ""}`}
    >
      <span className="mr-2">{icon}</span>
      {children}
    </Button>
  );
};
